/**
 * LRU for decoded Arrow tiles. Keys are `buildTileCacheKey()` strings:
 * `recordingId_startMs_endMs_level_speed_mode_...`
 *
 * Playback must not retain every window for the session. A 20-minute 8x
 * segment previously kept 24 decoded tiles plus React Query history, which
 * grew heap for the whole segment even after blit/reload isolation.
 */

export const DEFAULT_DECODED_TILE_CACHE_LIMIT = 4;

export function parseDecodedTileCacheStartMs(key: string): number | null {
  const parts = key.split("_");
  if (parts.length < 3) {
    return null;
  }
  const startMs = Number(parts[1]);
  return Number.isFinite(startMs) ? startMs : null;
}

export function createDecodedTileLruCache<T>(
  maxEntries = DEFAULT_DECODED_TILE_CACHE_LIMIT,
) {
  const cache = new Map<string, T>();

  const set = (key: string, value: T): void => {
    if (cache.has(key)) {
      cache.delete(key);
    }
    cache.set(key, value);
    while (cache.size > maxEntries) {
      const oldestKey = cache.keys().next().value;
      if (!oldestKey) {
        break;
      }
      cache.delete(oldestKey);
    }
  };

  const get = (key: string): T | null => {
    const cached = cache.get(key);
    if (cached === undefined) {
      return null;
    }
    cache.delete(key);
    cache.set(key, cached);
    return cached;
  };

  const pruneBeforeStartMs = (minStartMs: number): number => {
    let removed = 0;
    for (const key of Array.from(cache.keys())) {
      const startMs = parseDecodedTileCacheStartMs(key);
      if (startMs !== null && startMs < minStartMs) {
        cache.delete(key);
        removed += 1;
      }
    }
    return removed;
  };

  const clearRecording = (recordingId: string): void => {
    const prefix = `${recordingId}_`;
    for (const key of Array.from(cache.keys())) {
      if (key.startsWith(prefix)) {
        cache.delete(key);
      }
    }
  };

  const size = (): number => cache.size;

  return { set, get, pruneBeforeStartMs, clearRecording, size };
}
